import type { TableColumnsType } from 'antd';
import { Space, Table } from 'antd';
import React from 'react';
import { formatAllocationValue } from '../../utils/allocationSearchUtils';
import CheckboxDataInput, { SizedCheckboxDataInput } from '../../../components/checkboxDataInput/CheckboxDataInput';
import { USAGE_GROUP_PRODUCT_ALLOCATIONS_SECTION_KEY, USAGE_GROUP_WORK_ALLOCATIONS_SECTION_KEY } from '../../../Consts';
import { POLARIS_CUSTOMER_NAME } from '../../../../home/Consts';
import { IAdjustmentState } from '../../../../redux/types/IAdjustmentState';
import { AllocationDetailRowType, AllocationHeaderRowType } from "../../../types/usageTypes/IAllocationRowTypes";
import {
    ALLOCATION_TABLE_DEFAULT_PAGE_SIZE,
    ALLOCATION_TABLE_PAGE_SIZE_OPTIONS,
    DEFAULT_ALLOCATION_SEARCH_PAGE_SIZE
} from '../../../../redux/constants/AllocationSearchConstants';
import { AGENCY_LOOKUP } from '../../../../lookup/Consts';
import { ILookupDictionary } from '../../../../lookup/types/ILookupDictionary';
import { searchLookupValueByCode } from '../../../../lookup/services/LookupHelpers';

interface IAdjustmentMaintenancePageProps {
    searchResults: AllocationHeaderRowType[];
    viewData: any;
    componentInstance?: string;
    addIPAdjustmentDetails?: (row: AllocationDetailRowType) => void;
    selectAllAllocationHeaders?: (selectAll: boolean) => void;
    selectAllocationHeader?: (AllocationDetailRowType) => void;
    customer?: string;
    adjustment?: IAdjustmentState;
    isReadonly?: boolean;
    hasMore?: boolean;
    serverPageSize?: number;
    loadMore?: () => Promise<void>;
    lookupValues?: ILookupDictionary;
}

type ConfigurableAllocationDetailColumn = TableColumnsType<AllocationDetailRowType>[number] & {
    fieldName?: string;
};

const AllocationSelectionView: React.FC<IAdjustmentMaintenancePageProps> = (props: IAdjustmentMaintenancePageProps) => {
    const { viewData, componentInstance, customer, adjustment, lookupValues, isReadonly } = props;
    const isSelectionReadOnly = Boolean(isReadonly) || adjustment?.dataSource === 'Posted';

    const onClick = (value) => {
        const { addIPAdjustmentDetails } = props;
        if(isSelectionReadOnly) return;
        addIPAdjustmentDetails(value);
    }

    const onClickSelectAllAllocationHeaders = () => {
        const { selectAllAllocationHeaders } = props;
        const allSelected = data.length > 0 && data.every(item => item.IsSelected);
        selectAllAllocationHeaders?.(!allSelected);
    };

    const onClickSelectAllocationHeader = (value) => {
        const { selectAllocationHeader } = props;
        selectAllocationHeader(value);
    }

    const getSocietyAffiliation = (societyCode?: string) => {
        if (!societyCode) return '';
        const resolved = searchLookupValueByCode(AGENCY_LOOKUP, lookupValues, societyCode) || societyCode;
        return resolved.toLowerCase() === 'non society' ? 'N/S' : resolved;
    };

    const expandedRowRender = (record: AllocationHeaderRowType) => {
        const details = record.UsageDetails;
        const data: AllocationDetailRowType[] = details.map((item, index) => {
            switch (componentInstance) {
                case USAGE_GROUP_WORK_ALLOCATIONS_SECTION_KEY:
                    return {
                        key: index.toString(),
                        WorkName: item.WorkName,
                        WorkID: item.WorkID,
                        WorkDuration: item.WorkDuration,
                        Amount:formatAllocationValue(item.Amount),
                        IsIntray: item.IsIntray,
                        adjustmentFlag: record.adjustmentFlag
                    };
                case USAGE_GROUP_PRODUCT_ALLOCATIONS_SECTION_KEY:
                    return {
                        key: index.toString(),
                        WorkAllocationStatus: item.WorkAllocationStatus,
                        WorkName: item.WorkName,
                        WorkID: item.WorkID,
                        WorkDuration: item.WorkDuration,
                        Amount: formatAllocationValue(item.Amount),
                        IsIntray: item.IsIntray,
                        adjustmentFlag: record.adjustmentFlag
                    };
                default:
                    return {
                        key: index.toString(),
                        AllocationUsageHeaderID: item.AllocationUsageHeaderID,
                        IPNameNumber: item.IPNameNumber,
                        AccountNumber: item.AccountNumber,
                        Name: item.FullName,
                        Role: item.Role,
                        Amount: formatAllocationValue(item.Amount),
                        PointsAllocated: formatAllocationValue(item.PointsAllocated),
                        SharePct: formatAllocationValue(item.SharePct),
                        PayeeIPBaseNumber: item.PayeeIPBaseNumber,
                        PayeeIpNameNumber: item.PayeeIpNameNumber,
                        PayeeAccountNumber: item.PayeeAccountNumber,
                        PayeeName: item.PayeeName,
                        SocietyCode: item.SocietyCode,
                        PoolCode: item['PoolCode'],
                        WorkNumber: record.WorkNumber,
                        WorkName: record.WorkName,
                        UsageGroupTitle: record.UsageGroupTitle,
                        adjustmentFlag: record.adjustmentFlag
                    };
            }
        });

        const detailHeaderColumnRender = () => {
            switch (componentInstance) {
                case USAGE_GROUP_WORK_ALLOCATIONS_SECTION_KEY:
                    return [
                        { title: headersFields['workName'], dataIndex: 'WorkName', key: 'WorkName' },
                        { title: headersFields['workID'], dataIndex: 'WorkID', key: 'WorkID' },
                        { title: headersFields['workDuration'], dataIndex: 'WorkDuration', key: 'WorkDuration' },
                        { title: headersFields['amount'], dataIndex: 'Amount', key: 'Amount' },
                        { title: headersFields['isIntray'], dataIndex: 'IsIntray', key: 'IsIntray' }
                    ];
                case USAGE_GROUP_PRODUCT_ALLOCATIONS_SECTION_KEY:
                    return [
                        { title: headersFields['workAllocationStatus'], dataIndex: 'WorkAllocationStatus', key: 'WorkAllocationStatus' },
                        { title: headersFields['workName'], dataIndex: 'WorkName', key: 'WorkName' },
                        { title: headersFields['workID'], dataIndex: 'WorkID', key: 'WorkID' },
                        { title: headersFields['workDuration'], dataIndex: 'WorkDuration', key: 'WorkDuration' },
                        { title: headersFields['amount'], dataIndex: 'Amount', key: 'Amount' },
                        { title: headersFields['isIntray'], dataIndex: 'IsIntray', key: 'IsIntray' }
                        ];
                default:
                    return filterVisibleColumns([
                        {
                            title: "Actions",
                            dataIndex: 'operation',
                            key: 'operation',
                            render: (_, value) => {
                                return <>
                                    <Space onClick={() => onClick(value)} size="middle">
                                        { isSelectionReadOnly ? <p>Add Adjustment Details</p> : <a>Add Adjustment Details</a> }
                                    </Space>
                                </>
                            },
                        },
                        { title: viewData["accountNumber"], dataIndex: 'AccountNumber', key: 'AccountNumber', fieldName: 'accountNumber' },
                        { title: viewData["ipName"], dataIndex: 'Name', key: 'Name', fieldName: 'ipName' },
                        { title: viewData["ipiNumber"], dataIndex: 'IPNameNumber', key: 'IPNameNumber', fieldName: 'ipiNumber' },
                        { title: viewData["role"], dataIndex: 'Role', key: 'Role', fieldName: 'role' },
                        { title: viewData["payeeAccountNumber"], dataIndex: 'PayeeAccountNumber', key: 'PayeeAccountNumber', fieldName: 'payeeAccountNumber' },
                        { title: viewData["payeeNameNumber"], dataIndex: 'PayeeIpNameNumber', key: 'PayeeIpNameNumber', fieldName: 'payeeNameNumber' },
                        { title: viewData["payeeBaseNumber"], dataIndex: 'PayeeIPBaseNumber', key: 'PayeeIPBaseNumber', fieldName: 'payeeBaseNumber' },
                        { title: viewData["payeeName"], dataIndex: 'PayeeName', key: 'PayeeName', fieldName: 'payeeName' },
                        {
                            title: viewData["societyAffiliation"] ?? "Society Affiliation",
                            dataIndex: 'SocietyCode',
                            key: 'SocietyCode',
                            fieldName: 'societyAffiliation',
                            render: (societyCode: string) => getSocietyAffiliation(societyCode)
                        },
                        { title: viewData["sharePct"], dataIndex: 'SharePct', key: 'SharePct', fieldName: 'sharePct' },
                        { title: viewData["pointsAllocated"], dataIndex: 'PointsAllocated', key: 'PointsAllocated', fieldName: 'pointsAllocated' },
                        { title: viewData["amountAllocated"], dataIndex: 'Amount', key: 'Amount', fieldName: 'amountAllocated' },
                    ]);
            }
        };

        return <Table columns={detailHeaderColumnRender()} dataSource={data} pagination={false} />;
    };

    const isFieldVisible = (fieldName: string) => {
        const field = viewData.fields?.find(x =>
            x.componentInstance === componentInstance &&
            x.name === fieldName
        );

        return field ? !field.hidden : true;
    };

    const filterVisibleColumns = (
        columns: ConfigurableAllocationDetailColumn[]
    ): TableColumnsType<AllocationDetailRowType> =>
        columns.filter(column => !column.fieldName || isFieldVisible(column.fieldName));

    const getMappedFields = () => {
        if (!componentInstance) {
            return;
        }
        const filtered = viewData.fields.filter(x => x.componentInstance === componentInstance);
        const result: { [id: string]: string; } = {};
        filtered.forEach(element => {
            result[element.name] = element.data;
        });
        return result;
    }

    const headerColumnRender = () => {
        switch (componentInstance) {
            case USAGE_GROUP_WORK_ALLOCATIONS_SECTION_KEY:
                return [
                    { title: headersFields['allocationStatus'], dataIndex: 'Status', key: 'Status' },
                    { title: headersFields['allocationDate'], dataIndex: 'AllocationDate', key: 'AllocationDate' },
                    { title: headersFields['distribution'], dataIndex: 'Distribution', key: 'Distribution' },
                    { title: headersFields['description'], dataIndex: 'Description', key: 'Description' },
                    { title: headersFields['poolCode'], dataIndex: 'PoolCode', key: 'PoolCode' },
                    { title: headersFields['Segment'], dataIndex: 'Segment', key: 'Segment' },
                    { title: headersFields['amount'], dataIndex: 'TotalAmount', key: 'TotalAmount' },
                    { title: headersFields['adjustmentFlag'], dataIndex: 'AdjustmentFlag', key: 'AdjustmentFlag',
                            render: (key: string, record) => (
                        <CheckboxDataInput
                                        label=""
                                        fieldName=""
                                        value={record?.adjustmentFlag ? record.adjustmentFlag : false}
                                        changeData={() => {}}
                                        readonly={false}
                                        isHidden={false}
                                    />)
                     }
                ];
            case USAGE_GROUP_PRODUCT_ALLOCATIONS_SECTION_KEY:
                return [
                    { title: headersFields['allocationStatus'], dataIndex: 'Status', key: 'Status' },
                    { title: headersFields['allocationDate'], dataIndex: 'AllocationDate', key: 'AllocationDate' },
                    { title: headersFields['distribution'], dataIndex: 'Distribution', key: 'Distribution' },
                    { title: headersFields['description'], dataIndex: 'Description', key: 'Description' },
                    { title: headersFields['poolCode'], dataIndex: 'PoolCode', key: 'PoolCode' },
                    { title: headersFields['Segment'], dataIndex: 'Segment', key: 'Segment' },
                    { title: headersFields['amount'], dataIndex: 'TotalAmount', key: 'TotalAmount' },
                    { title: headersFields['productID1'], dataIndex: 'ProductNumber', key: 'ProductNumber' },
                    { title: headersFields['productName'], dataIndex: 'ProductName', key: 'ProductName' },
                    { title: headersFields['adjustmentFlag'], dataIndex: 'AdjustmentFlag', key: 'AdjustmentFlag',  
                        render: (key: string, record) => (
                        <CheckboxDataInput
                                        label=""
                                        fieldName=""
                                        value={record?.adjustmentFlag ? record.adjustmentFlag : false}
                                        changeData={() => {}}
                                        readonly={false}
                                        isHidden={false}
                                    />) }
                ];
            default:
                return [
                    {
                        title: (
                            <SizedCheckboxDataInput 
                            label=''
                            value={data.length > 0 && data.every(item => item.IsSelected)}
                            changeData={() => onClickSelectAllAllocationHeaders()}
                            fieldName="SelectAllAllocationHeaders"
                            readonly={isSelectionReadOnly}
                            />
                        ),
                        dataIndex: 'checkbox',
                        key: 'checkbox',
                        render: (_, value) => {
                            return <SizedCheckboxDataInput label=''
                                value={props.searchResults.filter(x => x.AllocationUsageHeaderId === value.AllocationUsageHeaderId)[0].IsSelected}
                                changeData={() => onClickSelectAllocationHeader(value)} 
                                fieldName='SelectAllocationHeader' 
                                readonly={isSelectionReadOnly}/>
                        }
                    },
                    { title: viewData["allocationHeaderID"], dataIndex: 'AllocationUsageHeaderId', key: 'AllocationUsageHeaderId' },
                    { title: viewData["poolCode"], dataIndex: 'PoolCode', key: 'PoolCode' },
                    { title: viewData["workNumber"], dataIndex: 'WorkNumber', key: 'WorkNumber' },
                    { title: viewData["workName"], dataIndex: 'WorkName', key: 'WorkName' },
                    { title: viewData["productNumber"], dataIndex: 'ProductNumber', key: 'ProductNumber' },
                    { title: viewData["usageGroupTitle"], dataIndex: 'UsageGroupTitle', key: 'UsageGroupTitle' },
                    { title: viewData["allocationDate"], dataIndex: 'AllocationDate', key: 'AllocationDate' },
                    { title: viewData["allocationWeight"], dataIndex: 'AllocationWeight', key: 'AllocationWeight' },
                    { title: viewData["totalAmountAllocated"], dataIndex: 'TotalAmount', key: 'TotalAmount' },
                ];
        }
    };

    const headersFields = getMappedFields();
    const data: AllocationHeaderRowType[] = props.searchResults ?? [];

    const [currentPage, setCurrentPage] = React.useState(1);
    const [tablePageSize, setTablePageSize] = React.useState(ALLOCATION_TABLE_DEFAULT_PAGE_SIZE);
    const [pendingPage, setPendingPage] = React.useState<number | null>(null);
    const [loadingNextPage, setLoadingNextPage] = React.useState(false);

    const serverPageSize = props.serverPageSize ?? DEFAULT_ALLOCATION_SEARCH_PAGE_SIZE;
    const syntheticTotal = props.hasMore ? data.length + serverPageSize : data.length;

    React.useEffect(() => {
        if (pendingPage === null) {
            return;
        }

        const requestedEnd = pendingPage * tablePageSize;

        if (data.length >= requestedEnd) {
            setCurrentPage(pendingPage);
            setPendingPage(null);
            setLoadingNextPage(false);
            return;
        }

        if (!props.hasMore) {
            const lastAvailablePage = Math.max(1, Math.ceil(data.length / tablePageSize));
            setCurrentPage(Math.min(pendingPage, lastAvailablePage));
            setPendingPage(null);
            setLoadingNextPage(false);
        }
    }, [data.length, pendingPage, props.hasMore, tablePageSize]);

    const onPaginationChange = (page: number, nextPageSize?: number) => {
        const effectivePageSize = nextPageSize ?? tablePageSize;

        if (effectivePageSize !== tablePageSize) {
            setTablePageSize(effectivePageSize);
            setCurrentPage(1);
            setPendingPage(null);
            setLoadingNextPage(false);
            return;
        }

        const requestedEnd = page * effectivePageSize;

        if (requestedEnd <= data.length || !props.hasMore) {
            setCurrentPage(page);
            return;
        }

        if (loadingNextPage) {
            return;
        }

        setPendingPage(page);
        setLoadingNextPage(true);

        props.loadMore?.().catch(() => {
            setPendingPage(null);
            setLoadingNextPage(false);
        });
    };

    return (
        <>
            <Table
                className="allocation-table"
                columns={headerColumnRender()}
                expandable={
                    {
                        expandedRowRender: ((record) => record.UsageDetails 
                                 && componentInstance != USAGE_GROUP_WORK_ALLOCATIONS_SECTION_KEY ? expandedRowRender(record) : undefined),
                        rowExpandable: ((record) => record.UsageDetails && record.UsageDetails.length > 0 &&
                                 componentInstance != USAGE_GROUP_WORK_ALLOCATIONS_SECTION_KEY)
                    }
                }
                dataSource={data}
                loading={loadingNextPage}
                size="small"
                scroll={{ x: "max-content" }}
                pagination={{
                    position: ['topRight', 'bottomRight'],
                    current: currentPage,
                    pageSize: tablePageSize,
                    pageSizeOptions: ALLOCATION_TABLE_PAGE_SIZE_OPTIONS,
                    showSizeChanger: true,
                    total: syntheticTotal,
                    showTotal: (_total, range) => `${range[0]}-${range[1]} of ${props.hasMore ? `${data.length}+` : data.length}`,
                    onChange: onPaginationChange
                }}
            />
        </>
    );
};

export default AllocationSelectionView;